import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import logo from "@/assets/images/UrbNetLogoSemFundo.png";
import { useTheme } from "@/hooks/use-theme";
import { useEffect, useRef } from "react";
import { LandingAtmosphere } from "@/components/landing/LandingAtmosphere";
import { LandingFooter } from "@/components/landing/LandingFooter";

export default function QuemSomosPage() {
  const navigate = useNavigate();

  // Page is always light — restore previous theme on unmount
  const { theme, setTheme } = useTheme();
  const originalTheme = useRef(theme);
  useEffect(() => {
    if (theme !== "light") {
      setTheme("light");
    }
  }, [theme, setTheme]);
  useEffect(() => {
    return () => setTheme(originalTheme.current);
  }, [setTheme]);

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden bg-transparent text-foreground">
      <LandingAtmosphere />

      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/40 bg-background/50 backdrop-blur md:backdrop-blur-2xl">
        <div className="relative mx-auto flex h-[80px] max-w-7xl items-center justify-between px-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
          <a href="/" className="flex items-center gap-3">
            <img src={logo} alt="UrbNet" className="h-10 w-auto" />
            <span className="font-heading text-lg font-bold tracking-tight text-foreground">
              UrbNet Ouvidoria
            </span>
          </a>
          <button
            onClick={() => navigate("/login")}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-primary-dark px-5 py-2 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:shadow-primary/30"
          >
            Acessar
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-3xl px-6 pt-[140px] pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Quem somos
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Conheça a UrbNet e nossa missão
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-12 space-y-6 leading-relaxed text-muted-foreground"
        >
          <p>
            A Marelli Desenvolvimentos Eireli, com sede em Cachoeiro de Itapemirim, Espírito Santo, nasceu de uma constatação simples: a maioria das cidades brasileiras enfrenta os mesmos problemas urbanos há décadas — não por falta de vontade da gestão pública, mas por falta de uma infraestrutura digital capaz de transformar reclamação em ação rastreável.
          </p>
          <p>
            O UrbNet é a resposta a essa lacuna. Somos uma plataforma gov-tech construída para conectar cidadãos, ouvidorias municipais e gestores públicos em um único ecossistema — com Inteligência Artificial validando evidências em tempo real, hierarquia institucional verificada, SLA público e auditabilidade completa para os órgãos de controle.
          </p>
          <p>
            Não somos uma adaptação de produto estrangeiro, nem uma cópia do que já existe no mercado brasileiro. O UrbNet foi desenhado do zero, pensando na realidade concreta das prefeituras — de municípios de 20 mil habitantes a capitais — e na necessidade real de transparência que os Tribunais de Contas estaduais estão exigindo com força crescente.
          </p>
          <p>
            Acreditamos que tecnologia urbana não deve ser privilégio de metrópoles com orçamento robusto. Por isso, construímos uma plataforma escalável, com planos que respeitam o porte de cada município, e uma arquitetura de dados que protege o cidadão — que usa o app de forma anônima — e ao mesmo tempo entrega ao gestor público a inteligência que ele precisa para agir.
          </p>
          <p>
            O UrbNet é, acima de tudo, uma aposta de que gestão urbana eficiente e transparência pública não são metas distantes — são o próximo passo natural de qualquer prefeitura que queira estar à frente do seu tempo.
          </p>
        </motion.div>
      </main>

      <LandingFooter />
    </div>
  );
}
